import React, { useState, useEffect, useLayoutEffect } from 'react';
import { View, StyleSheet, FlatList, SafeAreaView, Text, Image, TouchableOpacity } from 'react-native';
import { Searchbar, Card, Chip, useTheme, Divider, ActivityIndicator, Snackbar } from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { getCategoryWithItems } from '../../api/menuAPI';
import LoadingIndicator from '../../components/LoadingIndicator';
import ErrorMessage from '../../components/ErrorMessage';

const MenuCategoryScreen = ({ navigation, route }) => {
  const theme = useTheme();
  const { categoryId, categoryName } = route.params || {};
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Kategori verilerini yükle
  useEffect(() => {
    loadCategoryData();
  }, [categoryId]);

  // Kategori adını navigation başlığı olarak ayarla
  useLayoutEffect(() => {
    if (category?.name) {
      navigation.setOptions({
        title: category.name
      });
    } else if (categoryName) {
      navigation.setOptions({
        title: categoryName
      });
    }
  }, [navigation, category, categoryName]);
  
  // Arama filtresini uygula
  useEffect(() => {
    if (menuItems.length > 0) {
      if (searchQuery.trim() === '') {
        setFilteredItems(menuItems);
      } else {
        const lowercaseQuery = searchQuery.toLowerCase();
        const filtered = menuItems.filter(item => 
          item.name.toLowerCase().includes(lowercaseQuery) || 
          (item.description && item.description.toLowerCase().includes(lowercaseQuery))
        );
        setFilteredItems(filtered);
      }
    }
  }, [searchQuery, menuItems]);
  
  const loadCategoryData = async () => {
    if (!categoryId) {
      setError('Kategori bilgisi eksik');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await getCategoryWithItems(categoryId);
      
      if (response.success) {
        setCategory(response.category);
        setMenuItems(response.items);
        setFilteredItems(response.items);
      } else {
        setError('Kategori verileri alınamadı');
        setSnackbarMessage('Kategori verileri alınamadı');
        setSnackbarVisible(true);
      }
    } catch (err) {
      console.error('Kategori verileri yüklenirken hata:', err);
      setError('Kategori verileri yüklenirken bir hata oluştu');
      setSnackbarMessage('Kategori verileri yüklenirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setLoading(false);
    }
  };
  
  const renderMenuItem = ({ item }) => (
    <TouchableOpacity 
      style={styles.menuItem}
      onPress={() => navigation.navigate('MenuItem', { itemId: item.id })}
    >
      <Card style={styles.card}>
        <View style={styles.cardContent}>
          <View style={styles.imageContainer}>
            <Image 
              source={{ uri: item.image_url || 'https://picsum.photos/700' }} 
              style={styles.itemImage} 
              resizeMode="cover"
            />
          </View>
          <View style={styles.textContainer}>
            <Text style={styles.itemName}>{item.name}</Text>
            
            {item.description && (
              <Text style={styles.itemDescription} numberOfLines={2}>
                {item.description}
              </Text>
            )}
            
            <View style={styles.priceContainer}>
              <Text style={styles.itemPrice}>{item.price} ₺</Text>
              
              
            </View>
            
            {!item.is_available && (
              <Chip 
                mode="outlined" 
                style={styles.unavailableChip}
                textStyle={styles.unavailableChipText}
              >
                Tükendi
              </Chip>
            )}
          </View>
        </View>
      </Card>
    </TouchableOpacity>
  );
  
  if (loading) {
    return <LoadingIndicator message="Kategori yükleniyor..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage 
        message={error} 
        buttonText="Tekrar Dene" 
        onButtonPress={loadCategoryData} 
      />
    );
  }
  
  return (
    <SafeAreaView style={styles.container}>
      {/* 
      <View style={styles.contentHeader}>
        <Text style={styles.categoryTitle}>{category?.name || categoryName}</Text>
      </View>
      */}
      
      <Searchbar
        placeholder="Bu kategoride ara..."
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={styles.searchBar}
      />
      
      {filteredItems.length === 0 ? (
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons name="food-off" size={64} color="#ccc" />
          <Text style={styles.emptyText}>Bu kategoride ürün bulunamadı</Text>
          {searchQuery ? (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Text style={styles.clearSearch}>Aramayı Temizle</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : (
        <FlatList
          data={filteredItems}
          renderItem={renderMenuItem}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={styles.listContent}
        />
      )}
      
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        action={{
          label: 'Tamam',
          onPress: () => setSnackbarVisible(false),
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  contentHeader: {
    padding: 16,
    paddingTop: 20,
    backgroundColor: 'transparent',
  },
  categoryTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#333',
    marginBottom: 8,
  },
  searchBar: {
    margin: 16,
    borderRadius: 8,
    elevation: 2,
  },
  listContent: {
    padding: 8,
  },
  menuItem: {
    marginBottom: 12,
    marginHorizontal: 8,
  },
  card: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  cardContent: {
    flexDirection: 'row',
    padding: 10,
  },
  imageContainer: {
    width: 80,
    height: 80,
    borderRadius: 8,
    overflow: 'hidden',
    marginRight: 12,
  },
  itemImage: {
    width: '100%',
    height: '100%',
  },
  textContainer: {
    flex: 1,
    justifyContent: 'space-between',
  },
  itemName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  itemDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  itemPrice: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#3498db',
  },
  discountPrice: {
    fontSize: 14,
    color: '#999',
    textDecorationLine: 'line-through',
    marginLeft: 8,
  },
  unavailableChip: {
    marginTop: 4,
    backgroundColor: '#f8d7da',
    alignSelf: 'flex-start',
  },
  unavailableChipText: {
    color: '#721c24',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  emptyText: {
    fontSize: 16,
    color: 'black',
    textAlign: 'center',
    marginTop: 16,
  },
  clearSearch: {
    color: '#3498db',
    marginTop: 8,
    padding: 8,
  },
});

export default MenuCategoryScreen;
